import type { Reducer } from 'react';
import type { PaintPickerState } from './PaintPickerContext';
import type { PaintProduct } from '../type';
import type { PaintColourProduct } from '@modules/project/api';

export const LOCAL_STORAGE_ACTIVE_KEY = 'RESENE_PICKER_ACTIVE';
export const LOCAL_STORAGE_SEEN_HINT_KEY = 'RESENE_PICKER_SEEN_HINT';

export type PaintPickerAction =
    | { type: 'OPEN_PAINT_PICKER' }
    | { type: 'CLOSE_PAINT_PICKER' }
    | {
          type: 'TOGGLE_PICKING';
          payload: boolean;
      }
    | {
          type: 'SET_ACTIVE_COLOUR_INDEX';
          payload: number | null;
      }
    | {
          type: 'TOGGLE_POPULATE_DEFAULT';
          payload: boolean;
      }
    | {
          type: 'TOGGLE_USAGE_HINT';
          payload: boolean;
      }
    | {
          type: 'TOGGLE_ADDING_TO_CART';
          payload: boolean;
      }
    | {
          type: 'TOGGLE_SAVING_PALETTE';
          payload: boolean;
      }
    | {
          type: 'SET_PREVIEW_PAINT';
          payload: PaintProduct | null;
      }
    | {
          type: 'SET_PREVIEW_PAINT_MOBILE';
          payload: PaintColourProduct[] | null;
      }
    | { type: 'SET_COLOUR_CACHE'; payload: Map<string, PaintProduct> }
    | { type: 'SET_ACTIVE_IMAGE'; payload: string }
    | { type: 'SET_TOUCH_ENABLED'; payload: boolean };

export const paintPickerReducer: Reducer<
    PaintPickerState,
    PaintPickerAction
> = (state, action) => {
    switch (action.type) {
        case 'OPEN_PAINT_PICKER':
            setLocalStorageBooleanValue(LOCAL_STORAGE_ACTIVE_KEY, true);

            return {
                ...state,
                paintPickerActive: true,
            };

        case 'CLOSE_PAINT_PICKER':
            setLocalStorageBooleanValue(LOCAL_STORAGE_ACTIVE_KEY, false);

            return {
                ...state,
                paintPickerActive: false,
                isPicking: false,
                selectedColorIndex: null,
            };

        case 'TOGGLE_PICKING':
            setLocalStorageBooleanValue(LOCAL_STORAGE_ACTIVE_KEY, true);

            return {
                ...state,
                paintPickerActive: true,
                isPicking: action.payload,
                previewPaint: null,
            };

        case 'TOGGLE_POPULATE_DEFAULT':
            return {
                ...state,
                populateDefaults: action.payload,
            };

        case 'TOGGLE_USAGE_HINT':
            setLocalStorageBooleanValue(LOCAL_STORAGE_SEEN_HINT_KEY, true);

            return {
                ...state,
                usageHintEnabled: action.payload,
            };

        case 'SET_ACTIVE_COLOUR_INDEX':
            setLocalStorageBooleanValue(LOCAL_STORAGE_SEEN_HINT_KEY, true);

            return {
                ...state,
                isPicking: false,
                selectedColorIndex: action.payload,
                usageHintEnabled:
                    action.payload !== null ? false : state.usageHintEnabled,
            };

        case 'SET_PREVIEW_PAINT':
            return {
                ...state,
                previewPaint: action.payload,
            };

        case 'SET_PREVIEW_PAINT_MOBILE':
            const previewPaint = state.previewPaint;
            if (previewPaint && action.payload) {
                const previewWithProduct = action.payload.find(
                    (product) => product.ID === previewPaint.productId,
                );

                if (previewWithProduct && previewWithProduct.Link) {
                    previewPaint.link = previewWithProduct.Link;
                    return {
                        ...state,
                        previewPaint: previewPaint,
                    };
                }
            }
            return state;

        case 'SET_COLOUR_CACHE':
            return {
                ...state,
                colourCache: action.payload,
            };

        case 'SET_ACTIVE_IMAGE':
            return {
                ...state,
                activeImageSrc: action.payload,
            };

        case 'SET_TOUCH_ENABLED':
            return {
                ...state,
                touchEnabled: action.payload,
            };

        case 'TOGGLE_ADDING_TO_CART':
            return {
                ...state,
                isAddingToCart: action.payload,
            };

        case 'TOGGLE_SAVING_PALETTE':
            return {
                ...state,
                isSavingPalette: action.payload,
            };

        default:
            throw state;
    }
};

const setLocalStorageBooleanValue = (key: string, value: boolean): boolean => {
    if (typeof localStorage !== 'undefined' && localStorage) {
        localStorage.setItem(key, value ? 'true' : 'false');
        return true;
    }
    return false;
};
